import moment from "moment-timezone";
import { HUMAN_TIMEZONES } from "../../../../constants";

export const tzKeyFromIana = (iana) =>
    iana ? Object.entries(HUMAN_TIMEZONES).find(([, v]) => v === iana)?.[0] ?? "" : "";

export const ianaFromTzKey = (tzKey) => (tzKey && HUMAN_TIMEZONES[tzKey]) || tzKey || "UTC";

export const parseInTz = (dateStr, timeStr, tzKey) => {
    const timeZone = ianaFromTzKey(tzKey);
    return moment.tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm", timeZone);
};

/** Display send or pin-removal time using stored local date/time + timezone. */
export const formatScheduledLocalDisplay = (attrs, kind = "send") => {
    if (!attrs) return "";

    const isRemoval = kind === "removal";
    const date = isRemoval ? attrs.scheduledPinRemovalLocalDate : attrs.scheduledLocalDate;
    const time = isRemoval ? attrs.scheduledPinRemovalLocalTime : attrs.scheduledLocalTime;
    const iana = isRemoval
        ? attrs.scheduledPinRemovalTimezone || attrs.scheduledTimezone
        : attrs.scheduledTimezone;
    const isoFallback = isRemoval ? attrs.scheduledPinRemovalAt : attrs.scheduledAt;

    if (date && time && iana) {
        return moment.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", iana).format("MMM D, YYYY [at] h:mm A");
    }

    if (isoFallback) {
        const tz = iana || moment.tz.guess();
        return moment(isoFallback).tz(tz).format("MMM D, YYYY [at] h:mm A");
    }

    return "";
};

export const buildScheduleFormFromAttrs = (attrs, fallbackIana) => {
    const sendTzKey =
        tzKeyFromIana(attrs?.scheduledTimezone) ||
        tzKeyFromIana(fallbackIana) ||
        tzKeyFromIana(moment.tz.guess());
    const sendIana = ianaFromTzKey(sendTzKey);

    let date = attrs?.scheduledLocalDate || "";
    let time = attrs?.scheduledLocalTime || "";

    if ((!date || !time) && attrs?.scheduledAt) {
        const m = moment(attrs.scheduledAt).tz(sendIana);
        date = m.format("YYYY-MM-DD");
        time = m.format("HH:mm");
    }

    const hasPinRemoval = Boolean(attrs?.scheduledPinRemovalAt || attrs?.scheduledPinRemovalLocalDate);
    const removalTzKey =
        tzKeyFromIana(attrs?.scheduledPinRemovalTimezone) || sendTzKey;
    const removalIana = ianaFromTzKey(removalTzKey);

    let removalDate = attrs?.scheduledPinRemovalLocalDate || "";
    let removalTime = attrs?.scheduledPinRemovalLocalTime || "";

    if (hasPinRemoval && (!removalDate || !removalTime) && attrs?.scheduledPinRemovalAt) {
        const m = moment(attrs.scheduledPinRemovalAt).tz(removalIana);
        removalDate = m.format("YYYY-MM-DD");
        removalTime = m.format("HH:mm");
    } else if (hasPinRemoval && !removalDate) {
        const sendM = moment.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", sendIana);
        const m = sendM.add(1, "hour");
        removalDate = m.format("YYYY-MM-DD");
        removalTime = m.format("HH:mm");
    }

    return {
        tz: sendTzKey,
        date,
        time,
        pinMessage: Boolean(attrs?.pinOnSend),
        schedulePinRemoval: hasPinRemoval,
        removalTz: removalTzKey,
        removalDate,
        removalTime,
    };
};

export const buildScheduleApiFieldsFromForm = (values) => {
    const sendIana = ianaFromTzKey(values.tz);
    const removalTzKey = values.removalTz || values.tz;
    const removalIana = ianaFromTzKey(removalTzKey);

    const fields = {
        scheduledAt: parseInTz(values.date, values.time, values.tz).toISOString(),
        scheduledTimezone: sendIana,
        scheduledLocalDate: values.date,
        scheduledLocalTime: values.time,
    };

    if (values.pinMessage && values.schedulePinRemoval && values.removalDate && values.removalTime) {
        fields.scheduledPinRemovalAt = parseInTz(
            values.removalDate,
            values.removalTime,
            removalTzKey
        ).toISOString();
        fields.scheduledPinRemovalTimezone = removalIana;
        fields.scheduledPinRemovalLocalDate = values.removalDate;
        fields.scheduledPinRemovalLocalTime = values.removalTime;
    } else {
        fields.scheduledPinRemovalAt = null;
        fields.scheduledPinRemovalTimezone = null;
        fields.scheduledPinRemovalLocalDate = null;
        fields.scheduledPinRemovalLocalTime = null;
    }

    return fields;
};

export const buildPinRemovalApiFieldsFromForm = (values) => {
    const removalTzKey = values.removalTz || values.tz;
    const removalIana = ianaFromTzKey(removalTzKey);

    if (!values.schedulePinRemoval || !values.removalDate || !values.removalTime) {
        return {
            scheduledPinRemovalAt: null,
            scheduledPinRemovalTimezone: null,
            scheduledPinRemovalLocalDate: null,
            scheduledPinRemovalLocalTime: null,
        };
    }

    return {
        scheduledPinRemovalAt: parseInTz(values.removalDate, values.removalTime, removalTzKey).toISOString(),
        scheduledPinRemovalTimezone: removalIana,
        scheduledPinRemovalLocalDate: values.removalDate,
        scheduledPinRemovalLocalTime: values.removalTime,
    };
};
